import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { InventarioSoloUpload } from "../components/InventarioSoloUpload";
import api, { formatApiErrorDetail, formatMoney, formatNumber } from "../lib/api";
import { uploadSales } from "../lib/salesUpload";
import {
  UploadCloud, Loader2, CalendarRange, CheckCircle2, Package, FileSpreadsheet,
  CalendarDays, ArrowRight, RefreshCw, Trash2, Sparkles,
} from "lucide-react";

const TABS = [
  { key: "abcd", label: "Año + inventario (ABCD)", icon: Package },
  { key: "ventas", label: "Ventas mes a mes", icon: CalendarDays },
];

function SourceBadge({ status }) {
  if (!status) return null;
  const mensual = status.source === "mensual";
  return (
    <div
      className={`rounded-lg border px-4 py-3 text-sm flex items-start gap-3 ${mensual ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-slate-50 border-slate-200 text-slate-700"}`}
      data-testid="source-badge"
    >
      {mensual ? <Sparkles size={18} className="mt-0.5 shrink-0" /> : <CalendarRange size={18} className="mt-0.5 shrink-0" />}
      <div>
        <div className="font-semibold">
          {mensual ? "El ABCD ya se calcula con tus ventas mes a mes" : "El ABCD se calcula con la subida anual"}
        </div>
        <div className="text-xs mt-0.5">
          {mensual
            ? `${status.monthly_study?.name || "Estudio mensual"} · ${formatNumber(status.monthly_study?.total_products)} productos · periodo ${status.monthly_study?.period_days} días`
            : `Faltan ${status.monthly.missing_months} mes(es) consecutivos para cambiar automáticamente al mes a mes.`}
        </div>
      </div>
    </div>
  );
}

function MonthlyUpload({ onDone }) {
  const [file, setFile] = useState(null);
  const [tipo, setTipo] = useState("");
  const [busy, setBusy] = useState(false);
  const [secs, setSecs] = useState(0);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [anual, setAnual] = useState(false);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setErr("Elige el fichero de ventas del mes"); return; }
    setBusy(true); setErr(""); setMsg("");
    const fd = new FormData();
    fd.append("file", file);
    if (tipo) fd.append("tipo", tipo);
    if (anual) fd.append("dividir_meses", "true");
    try {
      const data = await uploadSales(fd, setSecs);
      setFile(null); setTipo("");
      if (data.dividido) {
        setMsg(`Dividido en ${data.meses.length} meses: ${data.meses.map((m) => m.label).join(", ")}.` +
          (data.sustituidos ? ` Se han sustituido ${data.sustituidos} subidas anteriores de esos meses.` : "") +
          (data.lineas_sin_fecha ? ` ${data.lineas_sin_fecha} líneas sin fecha no se han podido asignar a ningún mes.` : ""));
      } else {
        setMsg((data.monthly_source_active
          ? `Mes cargado: ${data.label}. Ya hay 12 meses seguidos → el ABCD pasa a calcularse con el mes a mes.`
          : `Mes cargado: ${data.label}.`) +
          (data.sustituidas?.length ? ` Se ha borrado la subida parcial anterior de este mes: ${data.sustituidas.join(", ")}.` : ""));
      }
      onDone?.();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "No se pudo subir el fichero");
    } finally { setBusy(false); setSecs(0); }
  };

  return (
    <form onSubmit={submit} className="card p-6 mb-6 space-y-3" data-testid="monthly-upload-card">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center">
          <CalendarDays size={18} />
        </div>
        <div>
          <h2 className="font-display text-lg font-bold">{anual ? "Subir varios meses o un año" : "Subir el mes"}</h2>
          <p className="text-xs text-slate-500">Diario de Farmatic, listado de Nixfarma o resumen por producto. El mes se detecta solo por las fechas del fichero.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.xlsm" className="hidden"
               onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(""); }} data-testid="monthly-file-input" />
        <button type="button" className="btn btn-outline" onClick={() => inputRef.current?.click()} data-testid="monthly-pick-file">
          <FileSpreadsheet size={14} /> {file ? file.name : "Elegir fichero"}
        </button>
        <select className="input" style={{ width: 230 }} value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="monthly-tipo-select">
          <option value="">Tipo (solo para resúmenes)</option>
          <option value="libre">Resumen venta libre</option>
          <option value="receta">Resumen receta</option>
        </select>
        <button type="submit" disabled={busy} className="btn btn-primary" data-testid="monthly-upload-button">
          {busy ? <><Loader2 size={14} className="animate-spin" /> Procesando{secs ? ` · ${secs} s` : "…"}</> : <><UploadCloud size={14} /> {anual ? "Subir año y dividir" : "Subir mes"}</>}
        </button>
      </div>
      <label className="flex items-start gap-2 text-sm cursor-pointer select-none" data-testid="monthly-anual-label">
        <input type="checkbox" className="mt-1" checked={anual} onChange={(e) => setAnual(e.target.checked)} data-testid="monthly-anual-checkbox" />
        <span>
          <b>Fichero de varios meses o de un año completo</b> (por ejemplo, el año anterior para comparar).
          <span className="block text-xs text-slate-500">Se usa la fecha de cada operación para dividirlo en meses. Si un mes ya estaba subido, se sustituye por el de este fichero.</span>
        </span>
      </label>
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="monthly-upload-error">{err}</div>}
      {msg && <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="monthly-upload-ok">{msg}</div>}
    </form>
  );
}

export default function Subidas() {
  const { view } = useParams();
  const navigate = useNavigate();
  const tab = TABS.some((t) => t.key === view) ? view : "abcd";
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rebuilding, setRebuilding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/uploads/status");
      setStatus(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const rebuild = async () => {
    setRebuilding(true); setError("");
    try {
      await api.post("/uploads/monthly-study/rebuild", {}, { timeout: 300000 });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setRebuilding(false); }
  };

  const delMonth = async (id) => {
    if (!window.confirm("¿Eliminar este mes?")) return;
    try { await api.delete(`/sales/imports/${id}`); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const m = status?.monthly;
  const pct = m ? Math.min(100, Math.round((m.consecutive_months / m.required_months) * 100)) : 0;

  return (
    <div className="p-8 fade-in" data-testid="subidas-page">
      <img src="/img/farmanaliza-subidas.jpg?v=2" alt="Farmanaliza · Centro de subidas: inventario, ventas y datos de tu farmacia"
           className="w-full max-w-xl rounded-2xl shadow-sm h-auto mb-4" data-testid="subidas-banner" />
      <h1 className="sr-only">Centro de subidas</h1>
      <p className="text-slate-600 mb-6">
        El estudio ABCD se alimenta de un año de ventas con el inventario. Las ventas e incentivos se suben mes a mes;
        al completar 12 meses seguidos, el ABCD pasa a usar el mes a mes automáticamente.
      </p>

      <div className="flex gap-2 mb-6 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => navigate(`/subidas/${t.key}`)}
                  className={`px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors flex items-center gap-2 ${tab === t.key ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
                  data-testid={`tab-${t.key}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="subidas-error">{error}</div>}
      {loading && <div className="text-slate-500 flex items-center gap-2" data-testid="subidas-loading"><Loader2 size={16} className="animate-spin" /> Cargando…</div>}

      {!loading && status && <div className="mb-6"><SourceBadge status={status} /></div>}

      {!loading && tab === "abcd" && (
        <div className="space-y-6" data-testid="subidas-abcd">
          <InventarioSoloUpload monthly={status.monthly} onDone={load} />
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/upload" className="card p-6 hover:border-slate-400 transition-colors block" data-testid="go-upload-excel">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-slate-900 text-white flex items-center justify-center"><UploadCloud size={18} /></div>
                <h2 className="font-display text-lg font-bold">Excel único (un año)</h2>
              </div>
              <p className="text-sm text-slate-600">Listado anual con ventas, existencias, PMC, PVP y caducidad. De aquí sale también el inventario.</p>
              <div className="text-sm font-semibold mt-3 inline-flex items-center gap-1">Subir <ArrowRight size={14} /></div>
            </Link>
            <Link to="/upload/farmatic" className="card p-6 hover:border-slate-400 transition-colors block" data-testid="go-upload-farmatic">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-10 w-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><FileSpreadsheet size={18} /></div>
                <h2 className="font-display text-lg font-bold">Farmatic (3 ficheros)</h2>
              </div>
              <p className="text-sm text-slate-600">Ventas del año + inventario + familias, tal y como los exporta Farmatic.</p>
              <div className="text-sm font-semibold mt-3 inline-flex items-center gap-1">Subir <ArrowRight size={14} /></div>
            </Link>
          </div>

          <div className="card p-6" data-testid="abcd-status">
            <h3 className="font-display text-lg font-bold mb-3">Último estudio con inventario</h3>
            {status.abcd.last_study ? (
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">Estudio</div>
                  <Link to={`/studies/${status.abcd.last_study.id}`} className="font-semibold hover:underline">{status.abcd.last_study.name}</Link>
                </div>
                <div><div className="text-xs uppercase tracking-widest text-slate-500">Productos</div><div className="font-semibold">{formatNumber(status.abcd.last_study.total_products)}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-slate-500">Periodo</div><div className="font-semibold">{status.abcd.last_study.period_days} días</div></div>
                <div><div className="text-xs uppercase tracking-widest text-slate-500">Valor stock</div><div className="font-semibold">{formatMoney(status.abcd.last_study.total_stock_value)}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-slate-500">Subido</div><div className="font-semibold">{status.abcd.last_study.created_at ? new Date(status.abcd.last_study.created_at).toLocaleDateString("es-ES") : "—"}</div></div>
              </div>
            ) : (
              <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2">
                Aún no has subido ningún año con inventario. Empieza por aquí.
              </div>
            )}
            <div className="text-xs text-slate-500 mt-3">{status.abcd.studies_count} estudio(s) anuales guardados · <Link to="/studies" className="underline">ver todos</Link></div>
          </div>
        </div>
      )}

      {!loading && tab === "ventas" && m && (
        <div className="space-y-6" data-testid="subidas-ventas">
          <MonthlyUpload onDone={load} />

          <div className="card p-6" data-testid="monthly-progress">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-2">
              <div>
                <h3 className="font-display text-lg font-bold">Meses consecutivos cargados</h3>
                <p className="text-xs text-slate-500">Al llegar a {m.required_months} meses seguidos, el ABCD se recalcula solo con el mes a mes.</p>
              </div>
              <div className="font-display text-3xl font-extrabold" data-testid="monthly-counter">{m.consecutive_months}/{m.required_months}</div>
            </div>
            <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${m.ready ? "bg-emerald-500" : "bg-slate-900"}`} style={{ width: `${pct}%` }} />
            </div>
            {m.ready && (
              <button onClick={rebuild} disabled={rebuilding} className="btn btn-outline btn-sm mt-4" data-testid="rebuild-monthly-button">
                {rebuilding ? <><Loader2 size={14} className="animate-spin" /> Recalculando…</> : <><RefreshCw size={14} /> Recalcular ABCD con los últimos 12 meses</>}
              </button>
            )}
          </div>

          <div className="card overflow-x-auto">
            <table className="dense" data-testid="months-table">
              <thead>
                <tr><th>Mes</th><th>Etiqueta</th><th>Desde</th><th>Hasta</th><th className="text-right">Facturación</th><th>En ventana</th><th></th></tr>
              </thead>
              <tbody>
                {m.months.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-6 text-slate-500">Aún no has subido ningún mes.</td></tr>
                ) : m.months.map((row) => (
                  <tr key={row.import_id} data-testid={`month-row-${row.month}`}>
                    <td className="font-semibold">{row.month_label}</td>
                    <td className="text-xs">{row.label}</td>
                    <td className="font-mono text-xs">{row.date_from ? new Date(row.date_from).toLocaleDateString("es-ES") : "—"}</td>
                    <td className="font-mono text-xs">{row.date_to ? new Date(row.date_to).toLocaleDateString("es-ES") : "—"}</td>
                    <td className="text-right font-mono text-xs">{formatMoney(row.facturacion)}</td>
                    <td>{(m.consecutive_window || []).includes(row.month)
                      ? <CheckCircle2 size={15} className="text-emerald-600" />
                      : <span className="text-xs text-slate-400">—</span>}</td>
                    <td className="text-right">
                      <Link to="/ventas" className="text-slate-600 hover:text-slate-900 text-xs underline mr-3">Analizar</Link>
                      <button onClick={() => delMonth(row.import_id)} className="text-red-600 hover:text-red-800 p-1" data-testid={`delete-month-${row.month}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
